"use client";

/*
 * The programme of an event, day by day: the helpers that read an entry
 * (scheduleEntryParts, entryPlace, climbingTexts, entryTitle), a line per entry
 * that opens in place (ScheduleRow) and the programme whole (EventSchedule). A
 * port of the current app's scheduleRow / scheduleEntryParts / scheduleClicks
 * in models/eventTemplates.js, for the public page and A minha inscrição alike.
 *
 * See public-api's scheduleService for what the app is given, which is less for
 * someone not registered: no meeting point, and nowhere's link or coordinates.
 */
import { useState, type ReactNode } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  ArrowDownRight,
  ArrowUpRight,
  Bed,
  Binoculars,
  ChevronDown,
  CircleAlert,
  ExternalLink,
  Flag,
  Info,
  LocationEdit,
  MapPinned,
  Route,
  Users,
  Utensils,
} from "lucide-react";
import { HikingIcon } from "@/components/ui/HikingIcon";
import { cdnUrl } from "@/lib/cdn";
import { addDays } from "@/lib/eventTime";
import { km, weekdayName } from "@/lib/format";
import type { EventSchedule as Schedule, ScheduleEntry, ScheduleEntryType, ScheduleTrail, Photo } from "@/lib/types/event";
import { DifficultyBar } from "@/components/event/DifficultyBar";
import { PhotoStrip } from "@/components/event/PhotoStrip";
import { SCHEDULE_ENTRY_LABELS, photoCount, trailTitle } from "@/components/event/labels";
import { galleryMedia } from "@/components/event/media";
import { mapsHref, safeHref } from "@/components/event/maps";

const ICONS: Record<ScheduleEntryType, typeof Flag> = {
  START: Flag,
  END: Flag,
  MEETING_POINT: Users,
  TEXT: Info,
  PICNIC: Utensils,
  LUNCH: Utensils,
  DINNER: Utensils,
  POI: Binoculars,
  TRAIL: HikingIcon,
  ACCOMMODATION: Bed,
};

export const entryIcon = (type: ScheduleEntryType) => ICONS[type] || Info;

/** 9500 -> "9,5 km", 12000 -> "12 km" */
export const scheduleKm = (metresWalked: number) => km(metresWalked / 1000);

/**
 * The trail's climbing as two short facts, "450 m de subida" and "200 m de
 * descida", each left out when nobody knows it.
 */
export const climbingTexts = (trail: Pick<ScheduleTrail, "ascent" | "descent">) => {
  const texts: string[] = [];
  const up = Math.round(Number(trail.ascent) || 0);
  const down = Math.round(Number(trail.descent) || 0);
  if (up > 0) {
    texts.push(`${up} m de subida`);
  }
  if (down > 0) {
    texts.push(`${down} m de descida`);
  }
  return texts;
};

/**
 * The way to wherever an entry happens, for an entry that has a place the data
 * can lead to: its own link or coordinates, those of the place or the stay it
 * points at, or the start of its trail. Nothing for the many that have none.
 */
export const entryPlace = (entry: ScheduleEntry) => {
  const places: [string | null | undefined, ScheduleEntry["location"]][] = [
    [entry.google_maps_link, entry.location],
    ...(entry.poi ? [[entry.poi.google_maps_link, null] as [string | null, null]] : []),
    ...(entry.accommodation ? [[entry.accommodation.google_maps_link, null] as [string | null, null]] : []),
    ...(entry.trail ? [[entry.trail.google_maps_link, entry.trail.starting_point] as [string | null, ScheduleEntry["location"]]] : []),
  ];
  return places.map(([link, location]) => mapsHref(link, location)).find((href) => /^https?:\/\//i.test(href)) || "";
};

export type EntryLink = { href: string; label: string; kind: "navigate" | "website" };

export type EntryFact = { icon: typeof Route; text: string };

export type EntryParts = {
  icon: typeof Flag;
  title: string;
  note: string;
  meta: EntryFact[];
  difficulty: string | null;
  links: EntryLink[];
  photos: Photo[];
  /** which set the photos are, for whoever shows more than one */
  photosOf: string;
};

const joinTexts = (...texts: (string | null | undefined)[]) =>
  texts
    .map((text) => String(text || "").trim())
    .filter(Boolean)
    .join("\n\n");

/**
 * What an entry is titled by, what it says under that, and the facts and links
 * that follow: a trail's distance and how hard it is, the town a stay is in,
 * the way to whatever it points at.
 */
export const scheduleEntryParts = (entry: ScheduleEntry): EntryParts => {
  const icon = entryIcon(entry.type);
  const label = SCHEDULE_ENTRY_LABELS[entry.type] ?? "";
  const content = String(entry.content || "").trim();
  let title = label;
  let note = content;
  const meta: EntryFact[] = [];
  let difficulty: string | null = null;
  let links: EntryLink[] = [{ href: mapsHref(entry.google_maps_link, entry.location), label: "Navegar", kind: "navigate" }];
  let photos: Photo[] = [];
  let photosOf = "";

  // its own words are all a text entry has, so they are what it is read by
  if (!label && !entry.poi && !entry.trail && !entry.accommodation) {
    title = content;
    note = "";
  }

  if (entry.poi) {
    title = entry.poi.title || "";
    // what the place says for itself, then what the programme says about it for this event
    note = joinTexts(entry.poi.description, content);
    links = [
      { href: mapsHref(entry.poi.google_maps_link, null), label: "Navegar", kind: "navigate" },
      { href: safeHref(entry.poi.website), label: "Saber mais", kind: "website" },
    ];
    photos = entry.poi.photos || [];
    photosOf = `poi:${entry.poi.id}`;
  }

  if (entry.trail) {
    title = trailTitle(entry.trail);
    note = joinTexts(entry.trail.short_description, content);
    if (Number(entry.trail.length) > 0) {
      meta.push({ icon: Route, text: scheduleKm(Number(entry.trail.length)) });
    }
    climbingTexts(entry.trail).forEach((text) => meta.push({ icon: text.endsWith("subida") ? ArrowUpRight : ArrowDownRight, text }));
    difficulty = entry.trail.difficulty;
    // a trail's videos play among its photos
    photos = galleryMedia(entry.trail.photos, entry.trail.videos);
    photosOf = `trail:${entry.trail.id}`;
  }

  if (entry.accommodation) {
    title = entry.accommodation.title || "";
    // the full address for someone going; the town alone for anyone else
    const where = [entry.accommodation.address, entry.accommodation.address_city].map((part) => String(part || "").trim()).filter(Boolean);
    if (where.length) {
      meta.push({ icon: MapPinned, text: where.join(", ") });
    }
    links = [
      { href: mapsHref(entry.accommodation.google_maps_link, null), label: "Navegar", kind: "navigate" },
      { href: safeHref(entry.accommodation.website), label: "Saber mais", kind: "website" },
    ];
    photos = entry.accommodation.photos || [];
    photosOf = `accommodation:${entry.accommodation.id}`;
  }

  // a place with nothing to say for itself still marks its time
  if (!String(title || "").trim()) {
    title = label || "";
  }

  return { icon, title, note, meta, difficulty, links: links.filter((link) => link.href), photos, photosOf };
};

/** what an entry is read by in a line: the place, the trail or the stay it points at, or the first line of its own words */
export const entryTitle = (entry: ScheduleEntry) => {
  const { title, note } = scheduleEntryParts(entry);
  const firstLine = (text: string) =>
    String(text || "")
      .split("\n")
      .map((line) => line.trim())
      .find(Boolean) || "";
  return entry.type === "MEETING_POINT" ? firstLine(note) || title : firstLine(title);
};

/** a programme's entries by day, in the order they arrive, which is the order they happen */
export const scheduleDays = (entries: ScheduleEntry[]) => {
  const days: { day: number; entries: ScheduleEntry[] }[] = [];
  entries.forEach((entry) => {
    let day = days.find((d) => d.day === entry.day);
    if (!day) {
      days.push((day = { day: entry.day, entries: [] }));
    }
    day.entries.push(entry);
  });
  return days;
};

/** the km of the trails among these entries, every one counted: "19 km", or "" */
export const dayKm = (entries: ScheduleEntry[]) => {
  const total = entries.reduce((sum, entry) => sum + ((entry.trail && Number(entry.trail.length)) || 0), 0);
  return total > 0 ? scheduleKm(total) : "";
};

/** "Domingo, dia 13", counting from the event's first day; "Dia 2" without a date */
export const scheduleDayLabel = (startDate: string | null | undefined, day: number) => {
  if (!startDate) {
    return `Dia ${day}`;
  }
  const date = addDays(startDate, day - 1);
  return `${weekdayName(date)}, dia ${Number(date.slice(8, 10))}`;
};

/** whether a row of this entry opens: when opening adds something to its closed line */
export const entryOpens = (entry: ScheduleEntry, { navigate = false } = {}) => {
  const { note, meta, difficulty, links, photos } = scheduleEntryParts(entry);
  const lines = note.split("\n").map((line) => line.trim()).filter(Boolean);
  const goes = navigate && !!entryPlace(entry);
  const more = (goes ? links.slice(1) : links).length;
  return !!(lines.length > 1 || meta.length || difficulty || more || photos.length);
};

/** whether any entry of these opens, for a view to offer Abrir tudo only when it would do something */
export const rowsOpen = (entries: ScheduleEntry[], options?: { navigate?: boolean }) => entries.some((entry) => entryOpens(entry, options));

/**
 * An entry as one line that opens in place. Closed, it is the time, the title,
 * the first line of what it says and what can be counted about it -- a trail's
 * km and climb, how many photos -- with a trail's first photo beside it. Open,
 * it is everything: the text, the facts, the ways out, the photos. An entry
 * opening would add nothing to has no chevron, and shows its line whole.
 *
 *  - open: where it starts; a change opens or closes it from outside (Abrir tudo)
 *  - sameTime: for an entry at the same time as the one before: no time, its title in the time's place
 *  - navigate: the way to the entry's place on its closed line, where it has one
 *  - onOpenTrail: given, a trail with a track gets Ver trilho
 *  - onOpenPhotos: told when a photo is pressed, before the gallery opens
 */
export function ScheduleRow({
  entry,
  open = false,
  sameTime = false,
  navigate = false,
  onOpenTrail,
  onOpenPhotos,
  trailAction,
}: {
  entry: ScheduleEntry;
  open?: boolean;
  sameTime?: boolean;
  navigate?: boolean;
  onOpenTrail?: (trail: ScheduleTrail, entry: ScheduleEntry) => void;
  onOpenPhotos?: (photos: Photo[], index: number, entry: ScheduleEntry) => void;
  /** something of the caller's own beside the trail's links */
  trailAction?: ReactNode;
}) {
  const parts = scheduleEntryParts(entry);
  const { icon: Icon, title, note, meta, difficulty, links, photos } = parts;
  const lines = note.split("\n").map((line) => line.trim()).filter(Boolean);
  const goes = navigate && entryPlace(entry);
  const opens = entryOpens(entry, { navigate });

  // opened or closed from outside, and then by its own chevron
  const [shown, setShown] = useState(open);
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    setShown(open);
  }
  const isOpen = opens && shown;

  // a trail's km and climb head its card; what is left to count is its photos
  const chips: string[] = [];
  const videos = photos.filter((photo) => photo.video).length;
  if (photos.length > videos) {
    chips.push(photoCount(photos.length - videos));
  }
  if (videos) {
    chips.push(`${videos} ${videos === 1 ? "vídeo" : "vídeos"}`);
  }
  const tone = TONES[entry.type] || TONES.TEXT;
  const { heading, body, tag } = cardWords(entry, title, lines);
  // a still beside the closed card: a video's frame there would promise a video the card does not play until opened
  const thumb = entry.trail && photos.length ? photos.find((photo) => !photo.video) || photos[0] : null;
  const showTrailButton = !!(onOpenTrail && entry.trail && entry.trail.has_track);
  const tagTone = entry.trail ? "text-[#c2710c]" : "text-ink-mute";
  const chevron = (
    <motion.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }} className="-mr-1 block shrink-0 text-ink-mute">
      <ChevronDown size={16} />
    </motion.span>
  );

  return (
    // scrolled to, it stops clear of what is fixed over the page: a phone's bar; on a
    // desktop the top bar and the event page's register bar that comes down under it
    <li id={`entry-${entry.id}`} className="relative scroll-mt-20 pl-10 desk:scroll-mt-36" data-entry={entry.id}>
      {/* the entry's mark, on the rail the list draws */}
      <span className={`absolute left-0 top-2.5 flex h-7 w-7 items-center justify-center rounded-full text-white ${tone.mark}`}>
        <Icon size={14} strokeWidth={2.4} />
      </span>
      <div>
        <div
          role={opens ? "button" : undefined}
          tabIndex={opens ? 0 : undefined}
          aria-expanded={opens ? isOpen : undefined}
          className={`py-3 ${opens ? "cursor-pointer" : ""}`}
          onClick={() => opens && setShown((v) => !v)}
          onKeyDown={(e) => {
            if (opens && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              setShown((v) => !v);
            }
          }}
        >
          {!sameTime && (entry.time || tag || opens) && (
            <div className="mb-1 flex items-center gap-2">
              <span className={`text-[13px] font-extrabold tabular-nums ${tone.time}`}>{entry.time}</span>
              <span className={`ml-auto text-right text-[12px] font-bold ${tagTone}`}>{tag}</span>
              {opens && chevron}
            </div>
          )}
          <div className="flex items-start gap-3">
            <span className="min-w-0 flex-1">
              <b className="block text-[15.5px] font-bold leading-snug">{heading}</b>
              {sameTime && tag && <span className={`mt-0.5 block text-[12px] font-bold ${tagTone}`}>{tag}</span>}
              {body && !isOpen && <span className={`mt-1 whitespace-pre-line text-[13.5px] leading-relaxed text-ink-soft ${opens ? "line-clamp-3" : "block"}`}>{opens ? body.replace(/\n+/g, " ") : body}</span>}
              {!isOpen && (chips.length > 0 || goes) && (
                <span className="mt-2 flex flex-wrap items-center gap-1.5">
                  {chips.map((chip) => (
                    <span key={chip} className="rounded-full bg-ground px-2 py-0.5 text-[11.5px] font-semibold text-ink-soft">
                      {chip}
                    </span>
                  ))}
                  {goes && (
                    <a
                      href={goes}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 rounded-full bg-brand-soft px-2 py-0.5 text-[11.5px] font-bold text-brand"
                    >
                      <LocationEdit size={11} /> Navegar
                    </a>
                  )}
                </span>
              )}
            </span>
            {thumb && !isOpen && (
              <span className="h-14 w-14 shrink-0 rounded-[10px] bg-line bg-cover bg-center" style={{ backgroundImage: `url('${thumbSrc(thumb)}')` }} />
            )}
            {sameTime && opens && <span className="mt-0.5">{chevron}</span>}
          </div>
        </div>
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              key="full"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
              className="overflow-hidden"
            >
              <div className="-mt-1.5 flex flex-col gap-2.5 pb-3.5">
                {body && <p className="whitespace-pre-line text-[13.5px] leading-relaxed text-ink-soft">{body}</p>}
                {(meta.length > 0 || difficulty) && (
                  <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 text-[13px] text-ink-soft">
                    {meta.map(({ icon: FactIcon, text }) => (
                      <span key={text} className="inline-flex items-center gap-1.5">
                        <FactIcon size={14} className="text-brand" /> {text}
                      </span>
                    ))}
                    <DifficultyBar difficulty={difficulty} />
                  </div>
                )}
                {(links.length > 0 || showTrailButton || trailAction) && (
                  <div className="flex flex-wrap items-center gap-2">
                    {links.map((link) => (
                      <a
                        key={link.kind}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pressable inline-flex items-center gap-1.5 rounded-full border border-line-strong px-3 py-1.5 text-[12.5px] font-semibold text-ink"
                      >
                        {link.kind === "navigate" ? <LocationEdit size={13} /> : <ExternalLink size={13} />} {link.label}
                      </a>
                    ))}
                    {showTrailButton && (
                      <button
                        type="button"
                        className="pressable inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-[12.5px] font-bold text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenTrail!(entry.trail!, entry);
                        }}
                      >
                        <MapPinned size={13} /> Ver trilho
                      </button>
                    )}
                    {trailAction}
                  </div>
                )}
                {photos.length > 0 && <PhotoStrip photos={photos} className="max-w-[420px]" onOpen={(index) => onOpenPhotos?.(photos, index, entry)} />}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </li>
  );
}

/** the list ScheduleRow's cards stand in: apart from each other, along a rail their marks sit on */
export const SCHEDULE_LIST = "relative flex flex-col gap-3 py-1 before:absolute before:bottom-4 before:left-[13px] before:top-4 before:w-0.5 before:rounded-full before:bg-brand/20";

/** the colour an entry's kind has on the rail and in its time */
const TONES: Record<ScheduleEntryType, { mark: string; time: string }> = {
  START: { mark: "bg-brand-dark", time: "text-brand-dark" },
  MEETING_POINT: { mark: "bg-brand-dark", time: "text-brand-dark" },
  TRAIL: { mark: "bg-brand", time: "text-brand-dark" },
  PICNIC: { mark: "bg-[#9a5b13]", time: "text-[#9a5b13]" },
  LUNCH: { mark: "bg-[#9a5b13]", time: "text-[#9a5b13]" },
  DINNER: { mark: "bg-[#9a5b13]", time: "text-[#9a5b13]" },
  POI: { mark: "bg-secondary", time: "text-secondary-dark" },
  ACCOMMODATION: { mark: "bg-secondary-dark", time: "text-secondary-dark" },
  TEXT: { mark: "bg-ink-mute", time: "text-ink-soft" },
  END: { mark: "bg-ink-mute", time: "text-ink-soft" },
};

const KIND_TAGS: Partial<Record<ScheduleEntryType, string>> = { POI: "Paragem", ACCOMMODATION: "Alojamento" };

/** as long as a first line may be and still head a card */
const HEADING_LENGTH = 60;

/**
 * What a card is headed by, what it says under that and the word at its top
 * right. A trail's is how far and how much it climbs. An entry that is a kind
 * of moment (the start, a meal) names its kind there when its own first line
 * is short enough to be the heading; otherwise the kind is the heading.
 */
const cardWords = (entry: ScheduleEntry, title: string, lines: string[]) => {
  if (entry.trail) {
    const up = Math.round(Number(entry.trail.ascent) || 0);
    const tag = [Number(entry.trail.length) > 0 ? scheduleKm(Number(entry.trail.length)) : "", up > 0 ? `+${up} m` : ""].filter(Boolean).join(" • ");
    return { heading: title, body: lines.join("\n\n"), tag };
  }
  const label = SCHEDULE_ENTRY_LABELS[entry.type];
  if (label && title === label && lines.length && lines[0].length <= HEADING_LENGTH) {
    return { heading: lines[0], body: lines.slice(1).join("\n\n"), tag: label };
  }
  return { heading: title, body: lines.join("\n\n"), tag: KIND_TAGS[entry.type] || "" };
};

const thumbSrc = (photo: Photo) => cdnUrl(photo.thumbnail || photo.path);

export const SCHEDULE_NOTE = "À exceção da hora de início e fim, os horários do programa são indicativos e podem ajustar-se ao ritmo do grupo e às condições do dia.";

/**
 * A day's title in a programme of several: large, in the brand's dark green
 * over a rule of its own, with the day's walking on the right, and set well
 * apart from the day before, so the days are what the eye finds first.
 */
export function ScheduleDayHeading({ label, km, first }: { label: string; km?: string | null; first: boolean }) {
  return (
    <h4 className={`mb-1 flex items-baseline justify-between gap-3 border-b-2 border-brand/30 pb-2 text-[17px] font-extrabold leading-tight text-brand-dark ${first ? "pt-1" : "pt-7"}`}>
      {label}
      {km && <span className="shrink-0 text-[13px] font-semibold text-ink-soft">{km}</span>}
    </h4>
  );
}

/**
 * The programme, a day at a time, each day's entries in the order they happen,
 * a line per entry opened in place. A day is headed by the date it falls on,
 * with how far it walks, but only when the event runs over more than one.
 * Abrir tudo opens every line, and is left out when no line opens. Ends saying
 * that only the start and the end are times to keep to.
 *
 *  - open: whether the lines start open (an event's page opens them, on a phone as on a desktop)
 *  - openEntry: one line that starts open whatever the others do, until Abrir tudo is pressed
 *  - day: that day alone, with no heading
 */
export function EventSchedule({
  schedule,
  open = false,
  openEntry = null,
  day = null,
  navigate = false,
  onOpenTrail,
  onOpenPhotos,
  className = "",
}: {
  schedule: Schedule;
  open?: boolean;
  openEntry?: number | null;
  day?: number | null;
  navigate?: boolean;
  onOpenTrail?: (trail: ScheduleTrail, entry: ScheduleEntry) => void;
  onOpenPhotos?: (photos: Photo[], index: number, entry: ScheduleEntry) => void;
  className?: string;
}) {
  const [allOpen, setAllOpen] = useState(open);
  const [pinned, setPinned] = useState(openEntry);
  const entries = schedule.entries || [];
  const days = scheduleDays(entries).filter((d) => day === null || Number(d.day) === Number(day));
  if (days.length === 0) {
    return null;
  }
  const headed = day === null && days.length > 1;
  const anyOpens = rowsOpen(entries, { navigate });

  return (
    <div className={className}>
      {anyOpens && (
        <div className="-mt-1 mb-1 flex justify-end">
          <button type="button" className="pressable text-[12.5px] font-bold text-brand" onClick={() => {
              setAllOpen((v) => !v);
              setPinned(null);
            }}
          >
            {allOpen ? "Fechar tudo" : "Abrir tudo"}
          </button>
        </div>
      )}
      {days.map((d, i) => (
        <div key={d.day}>
          {headed && <ScheduleDayHeading label={scheduleDayLabel(schedule.start_date, d.day)} km={dayKm(d.entries)} first={i === 0} />}
          <ul className={SCHEDULE_LIST}>
            {d.entries.map((entry, i) => (
              <ScheduleRow
                key={entry.id}
                entry={entry}
                sameTime={i > 0 && !!entry.time && entry.time === d.entries[i - 1].time}
                open={allOpen || entry.id === pinned}
                navigate={navigate}
                onOpenTrail={onOpenTrail}
                onOpenPhotos={onOpenPhotos}
              />
            ))}
          </ul>
        </div>
      ))}
      <p className="mt-3 flex items-start gap-2 text-[12.5px] leading-snug text-ink-mute">
        <CircleAlert size={14} className="mt-0.5 shrink-0" /> {SCHEDULE_NOTE}
      </p>
    </div>
  );
}
